import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCan } from '../../useCan';
import type { RequestOptions } from '../../../core/types/backend';
import { useLatestLoad } from '../../../hooks/useLatestLoad';
import { fetchInsightsOverview, fetchIntegrations } from '../../adminApi';
import { integrationInfo } from '../../../data/insightIntegrations';
import { ADMIN_BASE } from '../../../utils/hosts';
import { rangeLabel } from '../../sales/salesPeriods';
import { Kpi } from '../Kpi';
import { SignalList } from '../SignalList';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import type { PeriodState } from '../../sales/usePeriodParams';
import type { IntegrationId } from '../../../core/types/insights';

/**
 * Overzicht: eerst wat Sarah deze week anders kan doen (signalen), daarnaast de kerncijfers
 * met het verschil t.o.v. de vorige periode. Omzet rekent Omzet; hier staat alleen het totaal.
 */
export function OverviewTab({
  period
}: {
  period: PeriodState;
}) {
  const {
    canAll
  } = useCan();
  // Omzet en koppelingen: alleen met die bevoegdheid (Marketing ziet ze niet; de server laat ze weg).
  const seesRevenue = canAll('revenue.view');
  const managesIntegrations = canAll('integrations.manage');
  const report = useReport(fetchInsightsOverview, period.from, period.to);
  const loadIntegrations = useCallback((opts: RequestOptions) => managesIntegrations ? fetchIntegrations(opts) : Promise.resolve(null), [managesIntegrations]);
  const {
    data: integrations
  } = useLatestLoad(loadIntegrations);
  const failing = integrations ? (Object.keys(integrations) as IntegrationId[]).filter(id => integrations[id].status === 'fout') : [];
  return <ReportView report={report} what="De kerncijfers" data-id="element-2044" data-mp="arcgwjcu2ffq">
      {({
      kpis,
      signals
    }) => <>
          {failing.length > 0 && <p role="alert" className="rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-2045" data-mp="eu0pcpxmxkc6">
              Ophalen lukte niet voor {failing.map(id => integrationInfo[id].name).join(', ')}. Je ziet de laatst bekende cijfers.{' '}
              <Link to={`${ADMIN_BASE}/inzicht?tab=bereik&${period.query}`} className="underline underline-offset-2" data-id="element-2046" data-mp="2bt2bo7ahrvif">
                Bekijk koppelingen
              </Link>
            </p>}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-id="element-2047" data-mp="2ktqpb0vz3tus">
            <section aria-labelledby="signalen" className="rounded-lg border border-line bg-white p-5 sm:p-7" data-id="element-2048" data-mp="2krxgpg1suzk5">
              <h2 id="signalen" className="font-display text-[1.4rem] font-light text-ink" data-id="element-2049" data-mp="2ujjyjv8n7wvv">
                Wat je deze week kunt doen
              </h2>
              <p className="mb-5 mt-1 text-[13px] text-muted" data-id="element-2050" data-mp="38it3fyref4kj">Automatisch uit je eigen cijfers, de bezoekers en Google. Klik er een weg als het niet speelt.</p>
              <SignalList signals={signals} data-id="element-2051" data-mp="1e4s5dg3oalli" />
            </section>

            <section aria-labelledby="kerncijfers" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-2052" data-mp="1e74xgm35acca">
              <h2 id="kerncijfers" className="text-[15px] font-medium text-ink" data-id="element-2053" data-mp="3ktkc8bma4vsg">
                Kerncijfers
              </h2>
              <p className="mt-0.5 text-[12px] text-muted" data-id="element-2054" data-mp="3psx2758g6iz9">
                {rangeLabel(period.from, period.to)}, vergeleken met {rangeLabel(period.previous.from, period.previous.to)}
              </p>
              <dl className="mt-5 grid grid-cols-2 gap-x-5 gap-y-6" data-id="element-2055" data-mp="3g22gc1z45lec">
                {seesRevenue && <>
                    <Kpi label="Omzet webshop" value={kpis.revenueWebshopCents} kind="money" hint="Excl. btw" data-id="element-2056" data-mp="321hwr3ldjljs" />
                    <Kpi label="Omzet salon" value={kpis.revenueSalonCents} kind="money" hint="Excl. btw" data-id="element-2057" data-mp="3702umwd5lt52" />
                  </>}
                <Kpi label="Nieuwe klanten" value={kpis.newCustomers} data-id="element-2058" data-mp="2x3gbqd4e1c5o" />
                <Kpi label="Terugkerende klanten" value={kpis.returningCustomers} data-id="element-2059" data-mp="rb1iw3dyeemy" />
                <Kpi label="Bezoeken" value={kpis.visits} hint="Zonder cookies geteld" data-id="element-2060" data-mp="r8x3opaltt7d" />
                <Kpi label="Bestellingen per 100 bezoeken" value={kpis.ordersPer100Visits} kind="decimal" data-id="element-2061" data-mp="vbtrqviwyhip" />
                <Kpi label="Afspraken" value={kpis.bookings} hint="Aangevraagd en geboekt" data-id="element-2062" data-mp="6jw89dgxhuyp" />
                <Kpi label="Groei nieuwsbrief" value={kpis.newsletterNet} hint="Aanmeldingen min afmeldingen" data-id="element-2063" data-mp="1nnqrtf9dpod" />
              </dl>
              {seesRevenue && <Link to={`${ADMIN_BASE}/omzet?${period.query}`} className="mt-6 inline-block text-[13px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-2064" data-mp="3nt2giqxl8209">
                  Omzet in detail
                </Link>}
            </section>
          </div>
        </>}
    </ReportView>;
}